import React, { useState } from 'react';
import {
  Building2,
  Users,
  CreditCard,
  Bell,
  Wrench,
  Database,
  Plus,
  Trash2,
  CheckCircle2,
  Eye,
  Search,
  Package,
  Calendar,
  Vote,
  Shield,
  Clock,
  Receipt,
  Menu,
  Sparkles,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { AnnouncementCategory, CommonFeeBill } from '../types';
import { AdminSidebar, AdminTabKey } from './AdminSidebar';
import { ResidentDirectory } from './ResidentDirectory';
import { SlipVerificationModal } from './SlipVerificationModal';
import { ReceiptModal } from './ReceiptModal';
import { StaffLeaveManagement } from './StaffLeaveManagement';
import { SecurityPatrolMonitor } from './SecurityPatrolMonitor';
import { BoardAndReporting } from './BoardAndReporting';

// New Admin Sub-Module Views
import { AdminInventoryView } from './admin-views/AdminInventoryView';
import { AdminAssetsView } from './admin-views/AdminAssetsView';
import { AdminFeedbackView } from './admin-views/AdminFeedbackView';
import { AdminMembersAffairsView } from './admin-views/AdminMembersAffairsView';
import { AdminProjectInfoView } from './admin-views/AdminProjectInfoView';
import { AdminDeveloperView } from './admin-views/AdminDeveloperView';
import { AdminAgmVotingView } from './admin-views/AdminAgmVotingView';
import { AdminGovFilingView } from './admin-views/AdminGovFilingView';
import { AdminServicesView } from './admin-views/AdminServicesView';
import { AdminDownloadsView } from './admin-views/AdminDownloadsView';
import { AdminSettingsView } from './admin-views/AdminSettingsView';
import { AdminAnalyticsView } from './admin-views/AdminAnalyticsView';
import { ResidentInviteManager } from './admin-views/ResidentInviteManager';

interface Props {
  onOpenSupabaseModal: () => void;
}

export const AdminDashboard: React.FC<Props> = ({ onOpenSupabaseModal }) => {
  const {
    currentUser,
    announcements,
    addAnnouncement,
    deleteAnnouncement,
    bills,
    maintenanceTickets,
    updateMaintenanceStatus,
    parcels,
    addParcel,
    markParcelPickedUp,
    residents,
    leaveRequests,
    boardDiscussions,
    patrolLogs,
    isSupabaseConnected,
  } = useApp();

  const [activeTab, setActiveTab] = useState<AdminTabKey>('overview');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Announcement Modal
  const [showAnnModal, setShowAnnModal] = useState(false);
  const [annTitle, setAnnTitle] = useState('');
  const [annContent, setAnnContent] = useState('');
  const [annCategory, setAnnCategory] = useState<AnnouncementCategory>('general');
  const [annPinned, setAnnPinned] = useState(false);
  const [annImage, setAnnImage] = useState('');

  // Selected Bill for Slip Verification Modal
  const [selectedBillForVerification, setSelectedBillForVerification] = useState<CommonFeeBill | null>(null);

  // Selected Bill for Receipt Modal
  const [selectedBillForReceipt, setSelectedBillForReceipt] = useState<CommonFeeBill | null>(null);

  // New Parcel Modal
  const [showParcelModal, setShowParcelModal] = useState(false);
  const [parcelHouse, setParcelHouse] = useState('88/12');
  const [parcelResident, setParcelResident] = useState('คุณอนุกูล พรหมดี');
  const [parcelCarrier, setParcelCarrier] = useState('Kerry Express');
  const [parcelTracking, setParcelTracking] = useState('');
  const [parcelLocation, setParcelLocation] = useState('ชั้นวาง A-02');
  const [parcelSize, setParcelSize] = useState<'small' | 'medium' | 'large' | 'document'>('medium');

  // Sub-view for Finance tab
  const [financeSubTab, setFinanceSubTab] = useState<'bills' | 'board_reports'>('bills');

  // Counts
  const pendingVerificationBills = bills.filter((b) => b.status === 'pending_verification');
  const pendingLeaveCount = leaveRequests.filter((r) => r.status === 'pending').length;
  const pendingParcels = parcels.filter((p) => p.status === 'arrived');
  const openTickets = maintenanceTickets.filter((t) => t.status === 'in_progress' || t.status === 'pending');
  const activeBoardVotes = boardDiscussions.filter((d) => d.status === 'active_voting').length;

  const badgeCounts = {
    pendingBills: pendingVerificationBills.length,
    lowStock: 2,
    pendingFeedback: 1,
    pendingPetitions: 2,
    activeDefects: 1,
    activeVotes: activeBoardVotes,
  };

  const handleAddAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!annTitle || !annContent) return;

    await addAnnouncement({
      title: annTitle,
      content: annContent,
      category: annCategory,
      is_pinned: annPinned,
      author_name: currentUser?.full_name || 'นิติบุคคล นราวดี อิลิแกนซ์',
      image_url: annImage || undefined,
    });

    setShowAnnModal(false);
    setAnnTitle('');
    setAnnContent('');
    setAnnImage('');
    setAnnPinned(false);
  };

  const handleAddParcel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!parcelTracking) return;

    await addParcel({
      house_number: parcelHouse,
      resident_name: parcelResident,
      carrier: parcelCarrier,
      tracking_number: parcelTracking,
      shelf_location: parcelLocation,
      package_size: parcelSize,
      staff_name: currentUser?.full_name || 'เจ้าหน้าที่นิติบุคคล',
    });

    setShowParcelModal(false);
    setParcelTracking('');
  };

  return (
    <div id="admin-dashboard-wrapper" className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-4 space-y-4">
      {/* Mobile Sidebar Toggle Button */}
      <div className="lg:hidden flex items-center justify-between bg-slate-900 text-white p-3 rounded-2xl shadow-sm">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-blue-400" />
          <span className="font-bold text-xs truncate">เมนูจัดการนิติบุคคล (16 เมนู)</span>
        </div>
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="p-1.5 bg-slate-800 rounded-lg text-xs font-semibold flex items-center gap-1 text-slate-200"
        >
          <Menu className="w-4 h-4" />
          <span>เปิดเมนู</span>
        </button>
      </div>

      {/* Main Flex Layout: Left Sidebar + Right Content */}
      <div className="flex flex-col lg:flex-row gap-5 items-start">
        {/* Left-Hand Sidebar (Collapsible & Full/Icon mode) */}
        <AdminSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed}
          badgeCounts={badgeCounts}
          isMobileOpen={isMobileSidebarOpen}
          setIsMobileOpen={setIsMobileSidebarOpen}
        />

        {/* Right-Hand Content Area */}
        <main className="flex-1 min-w-0 space-y-6 w-full">
          {/* ======================= 1. OVERVIEW TAB ======================= */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Primary KPI Overview Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* Slips Pending */}
                <div
                  onClick={() => setActiveTab('finance')}
                  className="p-3.5 rounded-2xl border bg-white border-slate-200 hover:border-blue-400 cursor-pointer transition-all shadow-2xs"
                >
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>ตรวจสลิป</span>
                    <CreditCard className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="text-xl font-bold text-slate-800 mt-1 font-mono">
                    {pendingVerificationBills.length} <span className="text-xs font-normal text-slate-400 font-sans">รายการ</span>
                  </div>
                  <div className="text-[10px] text-amber-700 font-medium mt-0.5">
                    {pendingVerificationBills.length > 0 ? 'รออนุมัติยอด' : 'เรียบร้อยทั้งหมด'}
                  </div>
                </div>

                {/* Residents */}
                <div
                  onClick={() => setActiveTab('residents_registry')}
                  className="p-3.5 rounded-2xl border bg-white border-slate-200 hover:border-blue-400 cursor-pointer transition-all shadow-2xs"
                >
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>ลูกบ้าน</span>
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-xl font-bold text-slate-800 mt-1 font-mono">
                    {residents.length} <span className="text-xs font-normal text-slate-400 font-sans">หลัง</span>
                  </div>
                  <div className="text-[10px] text-blue-700 font-medium mt-0.5">ในสารบบทะเบียน</div>
                </div>

                {/* Parcels */}
                <div
                  onClick={() => setActiveTab('overview')}
                  className="p-3.5 rounded-2xl border bg-white border-slate-200 hover:border-blue-400 cursor-pointer transition-all shadow-2xs"
                >
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>พัสดุรอมารับ</span>
                    <Package className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="text-xl font-bold text-slate-800 mt-1 font-mono">
                    {pendingParcels.length} <span className="text-xs font-normal text-slate-400 font-sans">ชิ้น</span>
                  </div>
                  <div className="text-[10px] text-indigo-700 font-medium mt-0.5">จุดรับส่วนกลาง</div>
                </div>

                {/* Petitions / Member Affairs */}
                <div
                  onClick={() => setActiveTab('members_affairs')}
                  className="p-3.5 rounded-2xl border bg-white border-slate-200 hover:border-blue-400 cursor-pointer transition-all shadow-2xs"
                >
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>คำร้องสมาชิก</span>
                    <Calendar className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-xl font-bold text-slate-800 mt-1 font-mono">
                    {badgeCounts.pendingPetitions} <span className="text-xs font-normal text-slate-400 font-sans">คำขอ</span>
                  </div>
                  <div className="text-[10px] text-purple-700 font-medium mt-0.5">รอตรวจแบบแปลน</div>
                </div>

                {/* Security Patrol */}
                <div
                  onClick={() => setActiveTab('security')}
                  className="p-3.5 rounded-2xl border bg-white border-slate-200 hover:border-blue-400 cursor-pointer transition-all shadow-2xs"
                >
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>ตรวจลาดตระเวน</span>
                    <Shield className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="text-xl font-bold text-slate-800 mt-1 font-mono">
                    {patrolLogs.length} <span className="text-xs font-normal text-slate-400 font-sans">สแกน</span>
                  </div>
                  <div className="text-[10px] text-emerald-700 font-medium mt-0.5">7 จุดตรวจ QR</div>
                </div>

                {/* Board & Finance */}
                <div
                  onClick={() => setActiveTab('agm_voting')}
                  className="p-3.5 rounded-2xl border bg-white border-slate-200 hover:border-blue-400 cursor-pointer transition-all shadow-2xs"
                >
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>วาระโหวต AGM</span>
                    <Vote className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="text-xl font-bold text-slate-800 mt-1 font-mono">
                    {activeBoardVotes} <span className="text-xs font-normal text-slate-400 font-sans">วาระ</span>
                  </div>
                  <div className="text-[10px] text-teal-700 font-medium mt-0.5">เปิดลงมติ E-Vote</div>
                </div>
              </div>

              {/* Action Feed & Widgets */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left 2 Cols */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Slips Pending Widget */}
                  {pendingVerificationBills.length > 0 && (
                    <div className="bg-amber-50/90 border border-amber-200 rounded-2xl p-4 sm:p-5 space-y-3 shadow-xs">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
                          <CreditCard className="w-4 h-4 text-amber-600" />
                          สลิปโอนเงินรอตรวจสอบ ({pendingVerificationBills.length} รายการ)
                        </div>
                        <button
                          onClick={() => setActiveTab('finance')}
                          className="text-xs text-amber-800 hover:underline font-semibold cursor-pointer"
                        >
                          ดูและอนุมัติทั้งหมด →
                        </button>
                      </div>

                      <div className="space-y-2">
                        {pendingVerificationBills.map((b) => (
                          <div
                            key={b.id}
                            className="bg-white p-3 rounded-xl border border-amber-200 flex items-center justify-between text-xs"
                          >
                            <div>
                              <span className="font-bold text-slate-800">บ้านเลขที่ {b.house_number}</span>
                              <span className="text-slate-500 ml-2">({b.resident_name})</span>
                              <div className="text-[11px] text-slate-400 mt-0.5">งวด {b.period}</div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-blue-700 font-mono">฿{b.amount.toLocaleString()}</span>
                              <button
                                onClick={() => setSelectedBillForVerification(b)}
                                className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 cursor-pointer"
                              >
                                <Eye className="w-3.5 h-3.5" /> ตรวจสลิป
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Parcels Waiting Pick-up */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-4 shadow-xs">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                        <Package className="w-4 h-4 text-indigo-600" />
                        พัสดุรอมารับที่สำนักงาน ({pendingParcels.length} ชิ้น)
                      </h3>
                      <button
                        onClick={() => setShowParcelModal(true)}
                        className="text-xs text-blue-700 font-semibold hover:underline cursor-pointer"
                      >
                        + รับพัสดุเข้าใหม่
                      </button>
                    </div>

                    <div className="divide-y divide-slate-100">
                      {parcels.slice(0, 4).map((p) => (
                        <div key={p.id} className="py-2.5 flex items-center justify-between text-xs">
                          <div>
                            <span className="font-bold text-slate-800 font-mono">บ้าน {p.house_number}</span>
                            <span className="text-slate-600 ml-2">{p.resident_name}</span>
                            <div className="text-[11px] text-slate-400 font-mono">
                              {p.carrier} • {p.tracking_number} • {p.shelf_location}
                            </div>
                          </div>
                          {p.status === 'arrived' ? (
                            <button
                              onClick={() => markParcelPickedUp(p.id, currentUser?.full_name || 'จนท.นิติ')}
                              className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-[11px] font-semibold hover:bg-emerald-100 cursor-pointer"
                            >
                              ยืนยันการรับ
                            </button>
                          ) : (
                            <span className="text-emerald-600 text-[11px] font-medium">รับแล้ว</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Announcements Widget */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                        <Bell className="w-4 h-4 text-blue-600" />
                        ข่าวสารและประกาศโครงการ
                      </h3>
                      <button
                        onClick={() => setShowAnnModal(true)}
                        className="text-xs text-blue-700 font-semibold hover:underline cursor-pointer"
                      >
                        + สร้างประกาศ
                      </button>
                    </div>

                    <div className="space-y-2">
                      {announcements.slice(0, 2).map((ann) => (
                        <div key={ann.id} className="p-3 bg-slate-50 rounded-xl space-y-1 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-800">{ann.title}</span>
                            <span className="text-[10px] text-slate-400 font-mono">
                              {new Date(ann.created_at).toLocaleDateString('th-TH')}
                            </span>
                          </div>
                          <p className="text-slate-600 text-[11px] line-clamp-2">{ann.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right 1 Col */}
                <div className="space-y-4">
                  {/* Security Patrol Mini Status */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4 text-emerald-600" />
                        สถานะการลาดตระเวน
                      </h3>
                      <button
                        onClick={() => setActiveTab('security')}
                        className="text-xs text-emerald-600 font-semibold hover:underline cursor-pointer"
                      >
                        ดูผังตรวจ →
                      </button>
                    </div>

                    <div className="p-3 bg-slate-50 rounded-xl space-y-1.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-slate-500">การสแกน QR วันนี้:</span>
                        <span className="font-bold text-slate-800 font-mono">{patrolLogs.length} ครั้ง</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">ผลการตรวจปกติ:</span>
                        <span className="font-bold text-emerald-600 font-mono">
                          {patrolLogs.filter((l) => l.status === 'normal').length} จุด
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">เวรตรวจปัจจุบัน:</span>
                        <span className="font-medium text-blue-700">รปภ. สมศักดิ์ มั่งมี (ผลัดกลางวัน)</span>
                      </div>
                    </div>
                  </div>

                  {/* Estate Summary Box */}
                  <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
                    <h3 className="font-bold text-slate-900 text-sm">ข้อมูลสำนักงานนิติบุคคล</h3>
                    <div className="space-y-2 text-xs text-slate-600">
                      <div className="flex justify-between py-1.5 border-b border-slate-100">
                        <span>โครงการ:</span>
                        <span className="font-semibold text-slate-800">นราวดี อิลิแกนซ์ (อยุธยา)</span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b border-slate-100">
                        <span>ผู้จัดการนิติบุคคล:</span>
                        <span className="font-semibold text-blue-800">คุณศศิธร สุขสมบูรณ์</span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b border-slate-100">
                        <span>เบอร์สำนักงาน:</span>
                        <span className="font-mono text-slate-800">035-998-112</span>
                      </div>
                      <div className="flex justify-between py-1.5">
                        <span>สายตรวจ รปภ. 24 ชม.:</span>
                        <span className="font-mono font-bold text-rose-600">089-999-8888</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ======================= 2. FINANCE & ACCOUNTING TAB ======================= */}
          {activeTab === 'finance' && (
            <div className="space-y-6">
              {/* Header & Sub tabs */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h3 className="font-bold text-slate-900 text-base">
                    2. บัญชีและการเงิน (Accounting, Billing & E-Receipt Verification)
                  </h3>
                  <p className="text-xs text-slate-500">
                    ตรวจสลิปโอนเงิน, ออกใบเสร็จรับเงิน (E-Receipt / Print PDF) และงบการเงินนิติบุคคล
                  </p>
                </div>

                <div className="flex bg-slate-100 p-1 rounded-xl text-xs font-semibold text-slate-600">
                  <button
                    onClick={() => setFinanceSubTab('bills')}
                    className={`px-3 py-1.5 rounded-lg transition-all ${
                      financeSubTab === 'bills' ? 'bg-white text-blue-700 shadow-xs' : 'hover:text-slate-900'
                    }`}
                  >
                    ตรวจสอบสลิป & ใบเสร็จ ({pendingVerificationBills.length} รอตรวจ)
                  </button>
                  <button
                    onClick={() => setFinanceSubTab('board_reports')}
                    className={`px-3 py-1.5 rounded-lg transition-all ${
                      financeSubTab === 'board_reports' ? 'bg-white text-blue-700 shadow-xs' : 'hover:text-slate-900'
                    }`}
                  >
                    งบการเงิน & รายงานคณะกรรมการ
                  </button>
                </div>
              </div>

              {financeSubTab === 'bills' ? (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="p-3.5">บ้านเลขที่</th>
                          <th className="p-3.5">ชื่อลูกบ้าน</th>
                          <th className="p-3.5">งวดค่าส่วนกลาง</th>
                          <th className="p-3.5">ยอดชำระ</th>
                          <th className="p-3.5">สถานะ</th>
                          <th className="p-3.5">สลิปหลักฐาน</th>
                          <th className="p-3.5 text-right">การดำเนินการ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {bills.map((bill) => (
                          <tr key={bill.id} className="hover:bg-slate-50/60 transition-colors">
                            <td className="p-3.5 font-bold font-mono text-slate-900">{bill.house_number}</td>
                            <td className="p-3.5 font-medium text-slate-800">{bill.resident_name}</td>
                            <td className="p-3.5 text-slate-600">{bill.period}</td>
                            <td className="p-3.5 font-bold text-blue-700 font-mono">฿{bill.amount.toLocaleString()}</td>
                            <td className="p-3.5">
                              <span
                                className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                  bill.status === 'paid'
                                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                    : bill.status === 'pending_verification'
                                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                    : 'bg-rose-100 text-rose-800 border border-rose-200'
                                }`}
                              >
                                {bill.status === 'paid' && 'ชำระแล้ว'}
                                {bill.status === 'pending_verification' && 'รอตรวจสอบสลิป'}
                                {bill.status === 'unpaid' && 'รอบิลชำระ'}
                                {bill.status === 'overdue' && 'ค้างชำระ'}
                              </span>
                            </td>
                            <td className="p-3.5">
                              {bill.slip_url ? (
                                <button
                                  onClick={() => setSelectedBillForVerification(bill)}
                                  className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-medium text-[11px] flex items-center gap-1 border border-blue-200 transition-colors cursor-pointer"
                                >
                                  <Eye className="w-3 h-3 text-blue-600" />
                                  ดูสลิป
                                </button>
                              ) : (
                                <span className="text-slate-400 text-[11px]">-</span>
                              )}
                            </td>
                            <td className="p-3.5 text-right">
                              {bill.status === 'pending_verification' ? (
                                <button
                                  onClick={() => setSelectedBillForVerification(bill)}
                                  className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors cursor-pointer"
                                >
                                  พิจารณาอนุมัติ/ปฏิเสธ
                                </button>
                              ) : bill.status === 'paid' ? (
                                <button
                                  onClick={() => setSelectedBillForReceipt(bill)}
                                  className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-semibold border border-emerald-200 transition-colors inline-flex items-center gap-1.5 shadow-2xs cursor-pointer"
                                >
                                  <Receipt className="w-3.5 h-3.5 text-emerald-600" />
                                  <span>ดู/พิมพ์ใบเสร็จ (PDF)</span>
                                </button>
                              ) : (
                                <span className="text-[11px] text-slate-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <BoardAndReporting />
              )}
            </div>
          )}

          {/* ======================= 3. INVENTORY TAB ======================= */}
          {activeTab === 'inventory' && <AdminInventoryView />}

          {/* ======================= 4. ASSETS TAB ======================= */}
          {activeTab === 'assets' && <AdminAssetsView />}

          {/* ======================= 5. USER FEEDBACK TAB ======================= */}
          {activeTab === 'feedback' && <AdminFeedbackView />}

          {/* ======================= 6. SECURITY GUARD TAB ======================= */}
          {activeTab === 'security' && <SecurityPatrolMonitor />}

          {/* ======================= 7. MEMBERS AFFAIRS TAB ======================= */}
          {activeTab === 'members_affairs' && <><ResidentInviteManager /><AdminMembersAffairsView /></>}

          {/* ======================= 8. RESIDENTS REGISTRY TAB ======================= */}
          {activeTab === 'residents_registry' && <ResidentDirectory />}

          {/* ======================= 9. PROJECT INFO TAB ======================= */}
          {activeTab === 'project_info' && <AdminProjectInfoView />}

          {/* ======================= 10. DEVELOPER PORTAL TAB ======================= */}
          {activeTab === 'developer' && <AdminDeveloperView />}

          {/* ======================= 11. AGM & VOTING TAB ======================= */}
          {activeTab === 'agm_voting' && <AdminAgmVotingView />}

          {/* ======================= 12. GOVERNMENT FILING TAB ======================= */}
          {activeTab === 'gov_filing' && <AdminGovFilingView />}

          {/* ======================= 13. CONCIERGE & EXTRA SERVICES TAB ======================= */}
          {activeTab === 'services' && <AdminServicesView />}

          {/* ======================= 14. DOWNLOADABLE DOCUMENTS TAB ======================= */}
          {activeTab === 'downloads' && <AdminDownloadsView />}

          {/* ======================= 15. SETTINGS TAB ======================= */}
          {activeTab === 'settings' && (
            <AdminSettingsView
              onOpenSupabaseModal={onOpenSupabaseModal}
              isSupabaseConnected={isSupabaseConnected}
            />
          )}

          {/* ======================= 16. ANALYTICS & BI TAB ======================= */}
          {activeTab === 'analytics' && <AdminAnalyticsView />}
        </main>
      </div>

      {/* SLIP VERIFICATION MODAL */}
      {selectedBillForVerification && (
        <SlipVerificationModal
          bill={selectedBillForVerification}
          onClose={() => setSelectedBillForVerification(null)}
        />
      )}

      {/* RECEIPT VIEW / PRINT / PDF MODAL */}
      {selectedBillForReceipt && (
        <ReceiptModal
          bill={selectedBillForReceipt}
          onClose={() => setSelectedBillForReceipt(null)}
        />
      )}

      {/* MODAL: ADD PARCEL */}
      {showParcelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 bg-blue-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                <h3 className="font-bold text-base">บันทึกรับพัสดุเข้าโครงการ</h3>
              </div>
              <button
                onClick={() => setShowParcelModal(false)}
                className="p-1 rounded-lg text-slate-300 hover:text-white"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddParcel} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">บ้านเลขที่ *</label>
                  <select
                    value={parcelHouse}
                    onChange={(e) => {
                      setParcelHouse(e.target.value);
                      const res = residents.find((r) => r.house_number === e.target.value);
                      if (res) setParcelResident(res.owner_name);
                    }}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    {residents.map((r) => (
                      <option key={r.id} value={r.house_number}>
                        {r.house_number} ({r.owner_name})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ผู้รับ *</label>
                  <input
                    type="text"
                    value={parcelResident}
                    onChange={(e) => setParcelResident(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">บริษัทขนส่ง *</label>
                  <select
                    value={parcelCarrier}
                    onChange={(e) => setParcelCarrier(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="Kerry Express">Kerry Express</option>
                    <option value="Flash Express">Flash Express</option>
                    <option value="Shopee Xpress">Shopee Xpress</option>
                    <option value="Lazada Logistics">Lazada Logistics</option>
                    <option value="ไปรษณีย์ไทย (EMS)">ไปรษณีย์ไทย (EMS)</option>
                    <option value="J&T Express">J&T Express</option>
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ตำแหน่งจัดเก็บ *</label>
                  <input
                    type="text"
                    value={parcelLocation}
                    onChange={(e) => setParcelLocation(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">หมายเลขพัสดุ (Tracking Number) *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น KRYTH99218204"
                  value={parcelTracking}
                  onChange={(e) => setParcelTracking(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowParcelModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold shadow-md shadow-blue-600/20"
                >
                  บันทึกรับพัสดุ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD ANNOUNCEMENT */}
      {showAnnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 bg-blue-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                <h3 className="font-bold text-base">สร้างประกาศข่าวสารหมู่บ้าน</h3>
              </div>
              <button
                onClick={() => setShowAnnModal(false)}
                className="p-1 rounded-lg text-slate-300 hover:text-white"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddAnnouncement} className="p-6 space-y-4 text-xs">
              <div>
                <label className="font-semibold text-slate-700 block mb-1">หัวข้อประกาศ *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น กำหนดการฉีดพ่นยากันยุงประจำไตรมาส"
                  value={annTitle}
                  onChange={(e) => setAnnTitle(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">หมวดหมู่</label>
                  <select
                    value={annCategory}
                    onChange={(e) => setAnnCategory(e.target.value as any)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="general">ทั่วไป</option>
                    <option value="maintenance">งานซ่อมบำรุง</option>
                    <option value="event">กิจกรรม</option>
                    <option value="urgent">ด่วนมาก</option>
                  </select>
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={annPinned}
                      onChange={(e) => setAnnPinned(e.target.checked)}
                      className="rounded text-blue-600"
                    />
                    <span className="font-medium text-slate-700">ปักหมุดไว้บนสุด</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">เนื้อหาประกาศ *</label>
                <textarea
                  rows={4}
                  required
                  placeholder="กรอกรายละเอียดข่าวสาร..."
                  value={annContent}
                  onChange={(e) => setAnnContent(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">ลิงก์รูปภาพประกอบ (ทางเลือก)</label>
                <input
                  type="url"
                  placeholder="https://..."
                  value={annImage}
                  onChange={(e) => setAnnImage(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAnnModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold shadow-md shadow-blue-600/20"
                >
                  เผยแพร่ประกาศ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
